import { computed, Injectable, signal } from '@angular/core';
import { State, ToastPosition } from '@agilox/ui-common';
import { ToastMessage } from './models/toast-message.interface';

@Injectable()
export class ToastService {
	private readonly _toasts = signal<ToastMessage[]>([]);
	private readonly _timers = new Map<string, ReturnType<typeof setTimeout>>();

	readonly toasts = computed(() => this._toasts());

	show(message: string, type: State, duration: number = 5, position: ToastPosition = 'top') {
		const id = this.generateId();
		const toast: ToastMessage = { id, message, type, duration, position };
		this._toasts.update((toasts) => [...toasts, toast]);
		this.startTimer(id, duration);
	}

	dismiss(id: string) {
		const toast = this._toasts().find((t) => t.id === id);
		if (toast) {
			// Set dismissing flag to true to trigger the exit animation
			this._toasts.update((toasts) =>
				toasts.map((t) => (t.id === id ? { ...t, dismissing: true } : t))
			);

			// Apply exit animation before removing the toast after a delay
			const duration = toast.position === 'left' || toast.position === 'right' ? 300 : 200;
			setTimeout(() => {
				this._toasts.update((list) => list.filter((t) => t.id !== id));
				this.clearTimer(id);
			}, duration);
		}
	}

	pause(id: string) {
		this.clearTimer(id);
	}

	restart(id: string) {
		const toast = this._toasts().find((t) => t.id === id);
		if (toast?.duration) {
			this.clearTimer(id);
			this.startTimer(id, toast.duration);
		}
	}

	private startTimer(id: string, duration: number) {
		if (duration) {
			const timer = setTimeout(() => this.dismiss(id), duration * 1000);
			this._timers.set(id, timer);
		}
	}

	private clearTimer(id: string) {
		const timer = this._timers.get(id);
		if (timer) {
			clearTimeout(timer);
			this._timers.delete(id);
		}
	}

	private generateId(): string {
		return Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
	}
}
